import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, TableCellLayout,
  Button, Input, Switch, Dropdown, Option, Label, Field, Spinner, Textarea, Badge,
} from "@fluentui/react-components";
import { Delete20Regular, Add20Filled, Edit20Regular, Save20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../../Services/GraphServices";
import { getSharedSmsTemplates, saveSmsTemplate, deleteSmsTemplate } from "../../Services/HeedifyServices";
import { useNotification } from "../GenericComponent/NotificationSystem";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { measureSms } from "../../Utils/smsSegments";
import "./MailFlowsComponent.css";

/**
 * Gabarits SMS partagés — Quick SMS.
 *
 * Même forme que les gabarits mail, à une différence près qui commande tout l'écran :
 * **un SMS est facturé au segment**, et l'encodage décide de la capacité. 160 caractères en
 * GSM-7, mais 70 dès qu'un seul caractère en sort — et le `ç` MINUSCULE en sort, comme ê â î ô û,
 * œ, les guillemets français et l'apostrophe typographique que Word substitue en douce. Un
 * « ça » suffit donc à doubler la facture, sans que rien à l'écran ne change.
 *
 * D'où le compteur en direct sous la zone de saisie, qui n'annonce pas seulement le verdict
 * (« UCS-2 ») mais LES CARACTÈRES FAUTIFS : dire à un administrateur que son message est en
 * UCS-2 ne lui dit pas quoi corriger, lui montrer le « ç » le lui dit.
 *
 * Le comptage vient de `Utils/smsSegments`, transcription exacte de `SmsRender` côté serveur.
 * Les deux DOIVENT rester d'accord : si l'écran annonce un segment et que la passerelle en
 * facture deux, personne ne pourra expliquer la facture.
 */

const emptyTemplate = (scope) => ({
  scope: scope || "default",
  title: "",
  body: "",
  language: "fr",
  shortcut: "",
  enabled: true,
});

const SmsTemplatesComponent = ({ ACTIVE_TENANT, SCOPES_NAMES }) => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const { showSuccess, showError } = useNotification();

  const [scope, setScope] = useState("default");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  // Mesure en direct du corps en cours de saisie. Recalculée à chaque frappe : c'est le seul
  // moment où l'information sert — après l'enregistrement, il est trop tard pour reformuler.
  const live = useMemo(() => measureSms(editing?.body || ""), [editing]);

  const load = useCallback(async () => {
    if (!ACTIVE_TENANT) return;
    setLoading(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      setTemplates(await getSharedSmsTemplates(ACTIVE_TENANT, scope, token));
    } catch (e) {
      showError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [ACTIVE_TENANT, scope, instance, activeAccount, showError]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!String(editing.title || "").trim()) { showError(t("SMS_TEMPLATE_TITLE_REQUIRED")); return; }
    if (!String(editing.body || "").trim()) { showError(t("SMS_TEMPLATE_BODY_REQUIRED")); return; }
    setSaving(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      const { _rid, _self, _etag, _attachments, _ts, measure, ...payload } = editing;
      const result = await saveSmsTemplate(ACTIVE_TENANT, payload, token);
      if (!result.success) { showError(result.message); return; }
      showSuccess(t("SMS_TEMPLATE_SAVED"));
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (tpl) => {
    const token = await getAccessToken(instance, activeAccount);
    const result = await deleteSmsTemplate(ACTIVE_TENANT, tpl.id, token);
    if (!result.success) { showError(result.message); return; }
    showSuccess(t("SMS_TEMPLATE_DELETED"));
    setToDelete(null);
    await load();
  };

  const patch = (changes) => setEditing((tpl) => ({ ...tpl, ...changes }));

  /* ── Liste ──────────────────────────────────────────────────────────────────────────── */
  if (!editing) {
    return (
      <div className="mailflows-container">
        <div className="mailflows-header">
          <h2>{t("SMS_TEMPLATES")}</h2>
          <div className="mailflows-header-actions">
            {Array.isArray(SCOPES_NAMES) && SCOPES_NAMES.length > 0 && (
              <Dropdown
                value={scope}
                selectedOptions={[scope]}
                onOptionSelect={(_, d) => setScope(d.optionValue)}
                style={{ minWidth: 180 }}
              >
                {SCOPES_NAMES.map((s) => <Option key={s} value={s}>{s}</Option>)}
              </Dropdown>
            )}
            <Button appearance="primary" icon={<Add20Filled />} onClick={() => setEditing(emptyTemplate(scope))}>
              {t("ADD")}
            </Button>
          </div>
        </div>

        <p className="mailflows-hint">{t("SMS_TEMPLATE_SHARED_ONLY")}</p>

        {loading ? <Spinner label={t("LOADING")} /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t("NAME")}</TableHeaderCell>
                <TableHeaderCell>{t("MESSAGE")}</TableHeaderCell>
                <TableHeaderCell>{t("SMS_ENCODING")}</TableHeaderCell>
                <TableHeaderCell>{t("SMS_SEGMENTS")}</TableHeaderCell>
                <TableHeaderCell>{t("SHORTCUT")}</TableHeaderCell>
                <TableHeaderCell>{t("ENABLED")}</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((tpl) => {
                const m = tpl.measure || measureSms(tpl.body || "");
                return (
                  <TableRow key={tpl.id}>
                    <TableCell><TableCellLayout>{tpl.title}</TableCellLayout></TableCell>
                    <TableCell><TableCellLayout truncate>{String(tpl.body || "").slice(0, 60)}</TableCellLayout></TableCell>
                    <TableCell>
                      <TableCellLayout>
                        {/* L'UCS-2 n'est pas une erreur, c'est un coût. D'où « warning » et non
                            « danger » : l'administrateur doit le voir, pas le craindre. */}
                        <Badge appearance="tint" color={m.encoding === "GSM-7" ? "success" : "warning"}>
                          {m.encoding}
                        </Badge>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                        <Badge appearance="tint" color={m.segments > 1 ? "warning" : "informative"}>
                          {m.segments}
                        </Badge>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell><TableCellLayout>{tpl.shortcut || "—"}</TableCellLayout></TableCell>
                    <TableCell><TableCellLayout>{tpl.enabled === false ? t("NO") : t("YES")}</TableCellLayout></TableCell>
                    <TableCell>
                      <Button appearance="subtle" icon={<Edit20Regular />} onClick={() => setEditing(tpl)} />
                      <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => setToDelete(tpl)} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}><TableCellLayout>{t("NO_DATA_AVAILABLE")}</TableCellLayout></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <ConfirmAlert
          open={!!toDelete}
          name={toDelete?.title}
          onConfirm={() => onDelete(toDelete)}
          onCancel={() => setToDelete(null)}
        />
      </div>
    );
  }

  /* ── Édition ────────────────────────────────────────────────────────────────────────── */
  return (
    <div className="mailflows-container">
      <div className="mailflows-header">
        <h2>{editing.id ? t("EDIT") : t("ADD")} — {t("SMS_TEMPLATES")}</h2>
        <div className="mailflows-header-actions">
          <Button icon={<Dismiss20Regular />} onClick={() => setEditing(null)}>{t("CANCEL")}</Button>
          <Button appearance="primary" icon={<Save20Regular />} disabled={saving} onClick={onSave}>
            {saving ? <Spinner size="tiny" /> : t("SAVE")}
          </Button>
        </div>
      </div>

      <div className="mailflows-form">
        <div className="mailflows-inline">
          <Field label={t("NAME")} required>
            <Input value={editing.title || ""} onChange={(_, d) => patch({ title: d.value })} />
          </Field>
          <Field label={t("LANGUAGE")}>
            <Input value={editing.language || ""} onChange={(_, d) => patch({ language: d.value })} />
          </Field>
          <Field label={t("SHORTCUT")} hint={t("SMS_TEMPLATE_SHORTCUT_HINT")}>
            <Input value={editing.shortcut || ""} onChange={(_, d) => patch({ shortcut: d.value })} />
          </Field>
        </div>

        <Field label={t("MESSAGE")} required hint={t("SMS_TEMPLATE_BODY_HINT")}>
          <Textarea
            rows={5}
            value={editing.body || ""}
            onChange={(_, d) => patch({ body: d.value })}
            className="mailflows-mono"
          />
        </Field>

        {/* Le compteur. Il mesure le corps AVEC ses marqueurs : à l'envoi, `{{customerName}}`
            (16 caractères) devient un vrai nom souvent plus court, donc cette mesure est une
            borne HAUTE. Elle ne peut pas faire croire qu'un message tient alors qu'il déborde. */}
        <div className="mailflows-rules">
          <div className="mailflows-rules-head">
            <Label weight="semibold">{t("SMS_COST")}</Label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Badge appearance="tint" color={live.encoding === "GSM-7" ? "success" : "warning"}>
                {live.encoding}
              </Badge>
              <Badge appearance="tint" color={live.segments > 1 ? "warning" : "informative"}>
                {t("SMS_SEGMENTS")} : {live.segments}
              </Badge>
              <Badge appearance="tint" color={live.remaining < 0 ? "danger" : "informative"}>
                {live.length} / {live.capacity}
              </Badge>
            </div>
          </div>

          {live.forcedUcs2By.length > 0 && (
            <div className="mailflows-warning">
              {t("SMS_UCS2_WARNING")} <strong>{live.forcedUcs2By.join("  ")}</strong>
            </div>
          )}
          {live.segments > 1 && (
            <p className="mailflows-hint">{t("SMS_MULTISEGMENT_HINT", { n: live.segments })}</p>
          )}
        </div>

        {/* Les marqueurs disponibles. Une clef inconnue rend une chaîne VIDE côté serveur,
            jamais le marqueur — une faute de frappe est donc invisible dans le SMS reçu, et un
            SMS n'a pas d'objet derrière lequel se rattraper. */}
        <div className="mailflows-rules">
          <Label weight="semibold">{t("AVAILABLE_VARIABLES")}</Label>
          <p className="mailflows-hint">{t("SMS_TEMPLATE_VARIABLES_HINT")}</p>
          <code className="mailflows-code">
            {"{{customerName}} · {{greetingName}} · {{callerNumber}} · {{agentName}} · {{queueName}} · {{companyName}}"}
          </code>
        </div>

        <div className="mailflows-switch">
          <Label>{t("ENABLED")}</Label>
          <Switch checked={editing.enabled !== false} onChange={(_, d) => patch({ enabled: d.checked })} />
        </div>
      </div>
    </div>
  );
};

export default SmsTemplatesComponent;
